import React, { useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import NeuralField from "../components/landing/NeuralField";
import Closing from "../components/landing/Closing";
import { Container, Eyebrow, Display, Lead, Arrow, useReveal } from "../components/landing/ui";
import { colors, fonts, media, copperGradient, prefersReducedMotion } from "../styles/theme";
import { founder } from "./founderContent";
import { contact, links } from "./landingContent";

/* ---------- hero ---------- */

const Hero = styled.section`
  position: relative;
  overflow: hidden;
  padding: 150px 0 100px;
  background:
    radial-gradient(50% 60% at 100% 0%, rgba(216, 168, 137, 0.35), transparent 70%),
    radial-gradient(40% 50% at 0% 60%, rgba(163, 177, 143, 0.28), transparent 70%);
`;

const HeroGrid = styled.div`
  display: grid;
  gap: 48px;
  align-items: center;

  @media ${media.large} {
    grid-template-columns: minmax(0, 1.3fr) minmax(0, 0.9fr);
    gap: 80px;
  }
`;

const Title = styled.h1`
  font-family: ${fonts.display};
  font-weight: 300;
  font-size: clamp(52px, 7.4vw, 120px);
  line-height: 0.94;
  letter-spacing: -0.045em;
  margin: 22px 0 28px;
  color: ${colors.ink};

  em {
    display: block;
    font-style: normal;
    color: ${colors.copper};
  }
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 32px;

  a {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    height: 52px;
    padding: 0 24px;
    border-radius: 999px;
    font-family: ${fonts.body};
    font-size: 16px;
    font-weight: 600;
    transition: transform 0.3s ease;
  }
  a:hover {
    transform: translateY(-2px);
  }
  a.primary {
    color: ${colors.linen};
    background: ${copperGradient};
    box-shadow: 0 10px 30px -12px rgba(143, 83, 41, 0.7);
  }
  a.ghost {
    color: ${colors.ink};
    border: 1px solid ${colors.stone};
    background: rgba(250, 247, 241, 0.6);
  }
`;

const Portrait = styled.figure`
  position: relative;
  aspect-ratio: 4 / 5;
  max-width: 460px;
  width: 100%;
  justify-self: center;
  border-radius: 200px 200px 32px 32px;
  overflow: hidden;
  background: ${colors.forest};
  box-shadow: 0 50px 100px -50px rgba(30, 42, 34, 0.7);

  canvas {
    opacity: 0.75;
  }
  figcaption {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 28px;
    background: linear-gradient(180deg, transparent, rgba(30, 42, 34, 0.92));
    color: ${colors.linen};
  }
  figcaption b {
    display: block;
    font-family: ${fonts.display};
    font-weight: 400;
    font-size: 30px;
    letter-spacing: -0.02em;
  }
  figcaption span {
    font-family: ${fonts.mono};
    font-size: 12px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${colors.copperLight};
  }
`;

const Photo = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 30%;
  filter: saturate(0.92) sepia(0.06);
`;

const Monogram = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  font-family: ${fonts.display};
  font-style: normal;
  font-weight: 300;
  font-size: clamp(120px, 14vw, 180px);
  letter-spacing: -0.06em;
  color: rgba(250, 247, 241, 0.92);
  padding-bottom: 60px;
`;

/* ---------- numbers ---------- */

const Numbers = styled.section`
  padding: 90px 0;
  background: ${colors.forest};
  color: ${colors.linen};
`;

const NumbersHead = styled.div`
  display: grid;
  gap: 20px;
  margin-bottom: 48px;

  @media ${media.medium} {
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
    align-items: end;
  }

  blockquote {
    font-family: ${fonts.display};
    font-weight: 300;
    font-style: normal;
    font-size: clamp(34px, 4.6vw, 64px);
    line-height: 1.02;
    letter-spacing: -0.03em;
  }
  blockquote::before {
    content: "“";
    display: block;
    font-style: normal;
    font-size: 1.5em;
    line-height: 0.5;
    height: 0.4em;
    color: ${colors.copperLight};
  }
`;

const NumberGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  border-top: 1px solid rgba(250, 247, 241, 0.16);

  @media ${media.medium} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media ${media.large} {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }

  div {
    padding: 28px 18px 8px 0;
  }
  b {
    display: block;
    font-family: ${fonts.display};
    font-weight: 300;
    font-size: clamp(52px, 5vw, 76px);
    line-height: 1;
    letter-spacing: -0.05em;
    color: ${colors.copperLight};
    font-variant-numeric: tabular-nums;
  }
  span {
    display: block;
    margin-top: 10px;
    font-family: ${fonts.body};
    font-size: 15px;
    line-height: 1.45;
    color: rgba(250, 247, 241, 0.75);
    max-width: 180px;
  }
`;

/* ---------- then and now ---------- */

const Room = styled.section`
  padding: 130px 0 40px;
`;

const RoomHead = styled.div`
  display: grid;
  gap: 20px;
  margin-bottom: 44px;

  @media ${media.medium} {
    grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr);
    align-items: end;
  }
`;

const PairLabels = styled.div`
  display: none;

  @media ${media.medium} {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 64px minmax(0, 1fr);
    margin-bottom: 12px;
    font-family: ${fonts.mono};
    font-size: 12px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }
  span:first-child {
    color: ${colors.pebble};
  }
  span:last-child {
    grid-column: 3;
    color: ${colors.moss};
  }
`;

const Pair = styled.div`
  display: grid;
  gap: 10px;
  align-items: stretch;
  margin-bottom: 12px;

  @media ${media.medium} {
    grid-template-columns: minmax(0, 1fr) 64px minmax(0, 1fr);
    gap: 0;
  }

  p {
    padding: 22px 24px;
    border-radius: 20px;
    font-family: ${fonts.display};
    font-size: clamp(20px, 1.8vw, 24px);
    line-height: 1.25;
    letter-spacing: -0.01em;
  }
  p.then {
    background: ${colors.sand};
    color: ${colors.bark};
  }
  p.now {
    background: ${colors.forest};
    color: ${colors.linen};
  }
  small {
    display: block;
    margin-bottom: 6px;
    font-family: ${fonts.mono};
    font-size: 10px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    opacity: 0.7;

    @media ${media.medium} {
      display: none;
    }
  }
  i {
    display: none;

    @media ${media.medium} {
      display: grid;
      place-items: center;
      font-style: normal;
      font-size: 22px;
      color: ${colors.copper};
    }
  }
`;

/* ---------- timeline ---------- */

const TimelineWrap = styled.section`
  padding: 110px 0 140px;
`;

const Timeline = styled.ol`
  position: relative;
  list-style: none;
  margin-top: 64px;
  display: grid;
  gap: 28px;
  padding-left: 36px;

  @media ${media.medium} {
    padding-left: 0;
    gap: 0;
  }
`;

const Line = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 8px;
  width: 2px;
  background: ${colors.stone};

  span {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, ${colors.sage}, ${colors.moss} 30%, ${colors.terracotta} 45%, ${colors.ocean} 70%, ${colors.copper});
    transform-origin: 50% 0;
  }

  @media ${media.medium} {
    left: 50%;
    transform: translateX(-50%);
  }
`;

const Chapter = styled.li`
  position: relative;

  @media ${media.medium} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 80px;
    padding: 28px 0;
    align-items: center;
  }

  .dot {
    position: absolute;
    left: -36px;
    top: 30px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${colors.limestone};
    border: 3px solid ${(p) => p.$tone};

    @media ${media.medium} {
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }
  }

  .mark {
    display: none;

    @media ${media.medium} {
      display: block;
      grid-row: 1;
      grid-column: ${(p) => (p.$right ? 1 : 2)};
      justify-self: ${(p) => (p.$right ? "end" : "start")};
      font-family: ${fonts.display};
      font-style: normal;
      font-weight: 300;
      font-size: clamp(56px, 6vw, 96px);
      line-height: 1;
      letter-spacing: -0.05em;
      color: ${(p) => p.$tone};
      opacity: 0.35;
    }
  }

  article {
    padding: 30px 28px;
    border-radius: 24px;
    background: ${(p) => (p.$dark ? colors.forest : colors.linen)};
    color: ${(p) => (p.$dark ? colors.linen : colors.ink)};
    border: 1px solid ${(p) => (p.$dark ? colors.forest : colors.stone)};

    @media ${media.medium} {
      grid-row: 1;
      grid-column: ${(p) => (p.$right ? 2 : 1)};
    }
  }
  span.when {
    font-family: ${fonts.mono};
    font-size: 12px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${(p) => p.$tone};
  }
  h3 {
    margin: 12px 0 10px;
    font-family: ${fonts.display};
    font-weight: 400;
    font-size: clamp(26px, 2.4vw, 34px);
    line-height: 1.08;
    letter-spacing: -0.02em;
  }
  p {
    font-family: ${fonts.body};
    font-size: 17px;
    line-height: 1.6;
    color: ${(p) => (p.$dark ? "rgba(250,247,241,0.8)" : colors.bark)};
  }
`;

/* ---------- mission + connect ---------- */

const Mission = styled.section`
  padding: 0 0 120px;
`;

const MissionGrid = styled.div`
  display: grid;
  gap: 20px;

  @media ${media.large} {
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  }
`;

const MissionCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 32px;
  padding: 40px 28px;
  border-radius: 32px;
  background: ${colors.sand};

  @media ${media.medium} {
    padding: 56px;
  }

  .goal {
    font-family: ${fonts.display};
    font-weight: 300;
    font-size: clamp(44px, 5.2vw, 80px);
    line-height: 0.98;
    letter-spacing: -0.04em;
    color: ${colors.ink};
  }
  .goal em {
    display: block;
    font-style: normal;
    color: ${colors.copper};
  }
  .goalBody {
    margin-top: 18px;
    font-family: ${fonts.mono};
    font-size: 13px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${colors.bark};
  }
`;

const ConnectCard = styled.div`
  position: relative;
  overflow: hidden;
  padding: 40px 28px;
  border-radius: 32px;
  background: ${colors.forest};
  color: ${colors.linen};

  @media ${media.medium} {
    padding: 48px;
  }

  h3 {
    font-family: ${fonts.display};
    font-weight: 300;
    font-size: clamp(32px, 3vw, 44px);
    letter-spacing: -0.03em;
  }
  p {
    margin: 12px 0 28px;
    font-family: ${fonts.body};
    font-size: 17px;
    line-height: 1.55;
    color: rgba(250, 247, 241, 0.75);
  }
  ul {
    list-style: none;
    display: grid;
    gap: 10px;
  }
  a {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 18px;
    border-radius: 16px;
    background: rgba(250, 247, 241, 0.06);
    border: 1px solid rgba(250, 247, 241, 0.14);
    font-family: ${fonts.body};
    font-size: 16px;
    font-weight: 600;
    color: ${colors.linen};
    transition: background 0.2s ease, border-color 0.2s ease;
  }
  a small {
    display: block;
    font-family: ${fonts.mono};
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${colors.sage};
  }
  a:hover {
    background: rgba(250, 247, 241, 0.12);
    border-color: ${colors.copperLight};
  }
  a svg {
    color: ${colors.copperLight};
  }
`;

const TONES = [colors.sage, colors.moss, colors.terracotta, colors.ocean, colors.moss, colors.copperGlow, colors.copper];

const Founder = () => {
  const scope = useRef(null);
  useReveal(scope);

  useLayoutEffect(() => {
    document.title = "vimbal · Founding story";
    const ctx = gsap.context(() => {
      gsap.utils.toArray("[data-count]").forEach((el) => {
        const target = Number(el.dataset.count);
        const suffix = el.dataset.suffix || "";
        if (prefersReducedMotion()) return;
        const counter = { v: 0 };
        el.textContent = `0${suffix}`;
        gsap.to(counter, {
          v: target,
          duration: 1.8,
          ease: "power3.out",
          scrollTrigger: { trigger: el, start: "top 90%", once: true },
          onUpdate: () => {
            el.textContent = `${Math.round(counter.v)}${suffix}`;
          },
        });
      });
      if (prefersReducedMotion()) return;
      gsap.from("[data-f-hero] > *", { y: 30, opacity: 0, duration: 1, stagger: 0.08, ease: "power3.out" });
      gsap.from("[data-f-portrait]", { y: 40, opacity: 0, duration: 1.4, ease: "power3.out", delay: 0.2 });
      gsap.fromTo(
        "[data-f-line]",
        { scaleY: 0 },
        { scaleY: 1, ease: "none", scrollTrigger: { trigger: "[data-f-timeline]", start: "top 70%", end: "bottom 70%", scrub: true } }
      );
    }, scope);
    return () => ctx.revert();
  }, []);

  const [first, last] = founder.name.split(" ");
  const [photoOk, setPhotoOk] = useState(true);
  const room = founder.studyRoom;

  return (
    <div ref={scope}>
      <Hero aria-labelledby="f-title">
        <Container>
          <HeroGrid>
            <div data-f-hero>
              <div>
                <Eyebrow $color={colors.copper}>{founder.eyebrow}</Eyebrow>
              </div>
              <Title id="f-title">
                {founder.titleLead} <em>{founder.titleEmphasis}</em>
              </Title>
              <Lead>{founder.intro}</Lead>
              <HeroActions>
                <a className="primary" href="#journey">
                  Read the journey <Arrow />
                </a>
                <a className="ghost" href={contact.linkedin} target="_blank" rel="noopener noreferrer">
                  {contact.linkedinLabel} ↗
                </a>
              </HeroActions>
            </div>
            <Portrait data-f-portrait>
              {photoOk && founder.photo ? (
                <Photo
                  src={`${process.env.PUBLIC_URL}${founder.photo}`}
                  alt={`${founder.name}, ${founder.role}`}
                  onError={() => setPhotoOk(false)}
                />
              ) : (
                <>
                  <NeuralField variant="dark" calm={0.85} lines={9} />
                  <Monogram aria-hidden="true">
                    {first[0]}
                    {last[0]}
                  </Monogram>
                </>
              )}
              <figcaption>
                <b>{founder.name}</b>
                <span>{founder.role}</span>
              </figcaption>
            </Portrait>
          </HeroGrid>
        </Container>
      </Hero>

      <Numbers aria-label="By the numbers">
        <Container>
          <NumbersHead>
            <blockquote data-reveal>{founder.pullquote}</blockquote>
            <Lead $onDark data-reveal="0.1">
              Before vimbal was a company, it was one person's way through an impossible workload.
            </Lead>
          </NumbersHead>
          <NumberGrid>
            {founder.numbers.map((n, i) => (
              <div key={n.label} data-reveal={i * 0.06}>
                <b data-count={n.value} data-suffix={n.suffix}>
                  {n.value}
                  {n.suffix}
                </b>
                <span>{n.label}</span>
              </div>
            ))}
          </NumberGrid>
        </Container>
      </Numbers>

      <Room aria-labelledby="f-room">
        <Container>
          <RoomHead>
            <div data-reveal>
              <Eyebrow $color={colors.moss}>{room.eyebrow}</Eyebrow>
              <Display id="f-room" style={{ marginTop: 18 }}>
                {room.titleLead} <em>{room.titleEmphasis}</em>
              </Display>
            </div>
            <Lead data-reveal="0.1">{room.body}</Lead>
          </RoomHead>
          <PairLabels aria-hidden="true">
            <span>2015 · the study room</span>
            <span>Today · vimbal</span>
          </PairLabels>
          {room.pairs.map((pr, i) => (
            <Pair key={pr.now} data-reveal={i * 0.05}>
              <p className="then">
                <small>2015</small>
                {pr.then}
              </p>
              <i aria-hidden="true">→</i>
              <p className="now">
                <small>Today</small>
                {pr.now}
              </p>
            </Pair>
          ))}
        </Container>
      </Room>

      <TimelineWrap id="journey" aria-labelledby="f-journey">
        <Container>
          <div data-reveal>
            <Eyebrow $color={colors.moss}>The journey</Eyebrow>
            <Display id="f-journey" style={{ marginTop: 18, maxWidth: 900 }}>
              How a mining electrician <em>built a non-invasive brain–computer interface.</em>
            </Display>
          </div>
          <Timeline data-f-timeline>
            <Line aria-hidden="true">
              <span data-f-line />
            </Line>
            {founder.chapters.map((c, i) => (
              <Chapter
                key={c.title}
                $right={i % 2 === 1}
                $tone={TONES[i % TONES.length]}
                $dark={i === founder.chapters.length - 1}
              >
                <span className="dot" aria-hidden="true" />
                <span className="mark" aria-hidden="true">
                  {c.mark}
                </span>
                <article data-reveal>
                  <span className="when">{c.when}</span>
                  <h3>{c.title}</h3>
                  <p>{c.body}</p>
                </article>
              </Chapter>
            ))}
          </Timeline>
        </Container>
      </TimelineWrap>

      <Mission aria-label="Mission and contact">
        <Container>
          <MissionGrid>
            <MissionCard data-reveal>
              <div>
                <Eyebrow $color={colors.copper}>The mission</Eyebrow>
                <Lead style={{ marginTop: 18, maxWidth: 560 }}>{founder.missionQuote}</Lead>
              </div>
              <div>
                <p className="goal">
                  {founder.goalLead}
                  <em>{founder.goalEmphasis}</em>
                </p>
                <p className="goalBody">{founder.goalBody}</p>
              </div>
            </MissionCard>
            <ConnectCard data-reveal="0.1">
              <h3>{founder.connect.title}</h3>
              <p>{founder.connect.body}</p>
              <ul>
                <li>
                  <a href={contact.linkedin} target="_blank" rel="noopener noreferrer">
                    <span>
                      <small>LinkedIn</small>
                      linkedin.com/in/joshuaarnall
                    </span>
                    <Arrow />
                  </a>
                </li>
                <li>
                  <a href={`mailto:${contact.email}`}>
                    <span>
                      <small>Email</small>
                      {contact.email}
                    </span>
                    <Arrow />
                  </a>
                </li>
                <li>
                  <a href={contact.phoneHref}>
                    <span>
                      <small>Phone · {contact.phoneNote}</small>
                      {contact.phone}
                    </span>
                    <Arrow />
                  </a>
                </li>
                <li>
                  <Link to={links.register}>
                    <span>
                      <small>Work with vimbal</small>
                      Register interest
                    </span>
                    <Arrow />
                  </Link>
                </li>
              </ul>
            </ConnectCard>
          </MissionGrid>
        </Container>
      </Mission>

      <Closing title="Join the" emphasis="journey." />
    </div>
  );
};

export default Founder;
